/**
 * DashboardRouterController
 *
 * Mounted on /tableau-de-bord.html. Resolves the current org status via
 * GET /auth/me and routes the user to the right dashboard:
 *
 *   - 401            → /api.js global handler redirects to /connexion.html
 *   - org.status === "prospect"
 *                    → window.location.replace("/tableau-de-bord-prospect.html")
 *   - email_verified missing (defensive — should not happen post-2.3)
 *                    → /inscription-succes.html
 *   - org.status === "active"
 *                    → clone <template id="dashboard-payant-template">
 *                      (assemblé par EPIC-032.4 — 12 blocs ; le mode
 *                      `awaiting_siret` est géré côté dashboard-payant
 *                      à partir du payload `/dashboard/me`)
 *   - suspended / closed
 *                    → render the "Bientôt disponible" placeholder block
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class DashboardRouterController extends BaseController {
    static targets = ["loadingBlock", "comingSoonBlock", "orgStatus"];

    connect() {
        this.resolveAndRoute();
    }

    async resolveAndRoute() {
        let context;
        try {
            context = await api.getMe();
        } catch (error) {
            // 401 → api.js already kicked us to /connexion.html.
            // Anything else: surface as a coming-soon (we have a session
            // cookie but the backend errored).
            this.showComingSoon("indisponible");
            return;
        }

        const user = context?.user;
        const org = context?.organization;

        // Defensive: post-2.3 every login goes through /verify-email,
        // but if email_verified_at is null, fall back to the success page.
        if (user && user.emailVerifiedAt === null) {
            window.location.replace("/inscription-succes.html");
            return;
        }

        if (!org) {
            // No organization linked — treat as prospect (signup case).
            window.location.replace("/tableau-de-bord-prospect.html");
            return;
        }

        if (org.status === "prospect") {
            window.location.replace("/tableau-de-bord-prospect.html");
            return;
        }

        // EPIC-037 / 37.6 §25.F — vue dashboard archivage lecture seule.
        // Clone le template `dashboard-archived-readonly-template` à la
        // place de la vue payante quand l'organization est en
        // `archived_readonly`.
        const lifecycleState = await this._fetchLifecycleSafely();
        if (lifecycleState?.lifecycleStatus === "archived_readonly") {
            this.showArchivedReadonlyDashboard(lifecycleState);
            return;
        }

        if (org.status === "active") {
            this.showPayantDashboard();

            return;
        }

        // suspended / closed → not implemented yet.
        this.showComingSoon(org.status);
    }

    async _fetchLifecycleSafely() {
        try {
            return await api.getLifecycle();
        } catch {
            return null;
        }
    }

    showArchivedReadonlyDashboard(lifecycleState) {
        if (this.hasLoadingBlockTarget) {
            this.loadingBlockTarget.hidden = true;
        }
        if (this.hasComingSoonBlockTarget) {
            this.comingSoonBlockTarget.hidden = true;
        }

        const tpl = document.getElementById("dashboard-archived-readonly-template");
        if (!tpl) return;

        const anchor = this.hasLoadingBlockTarget
            ? this.loadingBlockTarget.parentNode
            : this.element;
        const fragment = tpl.content.cloneNode(true);
        anchor.appendChild(fragment);

        // Remplir les slots dynamiques : date d'anonymisation (10 ans
        // après la bascule — sera affinée en 37.7 quand le cron
        // d'anonymisation publiera l'event de préavis ; pour V1 on
        // affiche la date scheduled approximative).
        const anonymizationSlot = anchor.querySelector('[data-slot="date-anonymisation"]');
        if (anonymizationSlot && lifecycleState?.resiliationScheduledAt) {
            const basculeDate = new Date(lifecycleState.resiliationScheduledAt);
            if (!isNaN(basculeDate.getTime())) {
                const anonymizationDate = new Date(basculeDate);
                anonymizationDate.setFullYear(anonymizationDate.getFullYear() + 10);
                anonymizationSlot.textContent = anonymizationDate.toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                });
            }
        }

        // Bouton export RGPD — déclenche le téléchargement via l'API
        // RGPD existante (EPIC-036).
        const rgpdBtn = anchor.querySelector('[data-slot="rgpd-download-link"]');
        if (rgpdBtn) {
            rgpdBtn.addEventListener("click", (event) => {
                event.preventDefault();
                window.location.href = api.rgpdExportUrl();
            });
        }

        // EPIC-037 / 37.7 §25.I cas 2 — bouton « Réactiver mon abonnement ».
        // Appelle POST /organization/lifecycle/reactivate. La réponse
        // donne le solde d'avoir + les mandats à re-signer ; on redirige
        // vers la page de souscription parcours B raccourci.
        const reactivateBtn = anchor.querySelector('[data-slot="reactivate-btn"]');
        const reactivateError = anchor.querySelector('[data-slot="reactivate-error"]');
        const cessationBlock = anchor.querySelector('[data-slot="reactivate-cessation-block"]');
        if (reactivateBtn) {
            reactivateBtn.addEventListener("click", async (event) => {
                event.preventDefault();
                if (reactivateError) {
                    reactivateError.hidden = true;
                    reactivateError.textContent = "";
                }
                reactivateBtn.disabled = true;
                reactivateBtn.setAttribute("aria-busy", "true");
                try {
                    await api.reactivateOrganization();
                    // Réactivation OK → on enchaîne sur le checkout
                    // parcours B raccourci (le frontend de paiement est
                    // mutualisé avec l'onboarding initial).
                    window.location.href = "/abonnement.html?from=reactivate";
                } catch (err) {
                    reactivateBtn.disabled = false;
                    reactivateBtn.removeAttribute("aria-busy");
                    const code = err?.errorCode || err?.code || "";
                    if (code === "cessation_blocks_reactivation" && cessationBlock) {
                        cessationBlock.hidden = false;
                        cessationBlock.open = true;
                        reactivateBtn.hidden = true;
                        return;
                    }
                    if (reactivateError) {
                        reactivateError.hidden = false;
                        reactivateError.textContent =
                            err?.message
                                || "Impossible de réactiver le compte. Réessayez dans quelques instants.";
                    }
                }
            });
        }
    }

    showComingSoon(status) {
        if (this.hasLoadingBlockTarget) {
            this.loadingBlockTarget.hidden = true;
        }
        if (this.hasComingSoonBlockTarget) {
            this.comingSoonBlockTarget.hidden = false;
        }
        if (this.hasOrgStatusTarget) {
            this.orgStatusTarget.textContent = status || "—";
        }
    }

    showPayantDashboard() {
        if (this.hasLoadingBlockTarget) {
            this.loadingBlockTarget.hidden = true;
        }
        if (this.hasComingSoonBlockTarget) {
            this.comingSoonBlockTarget.hidden = true;
        }

        const tpl = document.getElementById("dashboard-payant-template");
        if (!tpl) return;

        const anchor = this.hasLoadingBlockTarget
            ? this.loadingBlockTarget.parentNode
            : this.element;
        anchor.appendChild(tpl.content.cloneNode(true));
    }
}
